import { Settings01 } from "@untitledui/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { getUserTimezones, saveUserTimezones } from "@/services/timezones";
import type { Timezone } from "@/store/roleSlice";
import { selectTimezones, setTimezones } from "@/store/roleSlice";

const VISIBLE_COUNT = 5;

const TimezoneBar = ({ upn }: { upn: string }): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const cities: Timezone[] = useSelector(selectTimezones);
  const dispatch = useDispatch();
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const hiddenRef = useRef<HTMLDivElement | null>(null);

  const [times24, setTimes24] = useState<Record<string, string>>({});
  const [times12, setTimes12] = useState<Record<string, string>>({});
  const [officeHours, setOfficeHours] = useState<Record<string, boolean>>({});

  const [savedCities, setSavedCities] = useState<Timezone[] | null>(null);

  const [manageOpen, setManageOpen] = useState(false);
  const [localCities, setLocalCities] = useState<Timezone[]>([]);
  const [dates, setDates] = useState<Record<string, string>>({});

  const activeCities = manageOpen ? localCities : (savedCities ?? cities);

  const visibleCities = activeCities.slice(0, VISIBLE_COUNT);
  const hiddenCities = activeCities.slice(VISIBLE_COUNT);

  useEffect(() => {
    setLocalCities(cities);
  }, [cities]);

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    e.dataTransfer.setData("index", String(index));
  };

  const onDrop = (index: number) => (e: React.DragEvent) => {
    const from = Number(e.dataTransfer.getData("index"));
    const updated = [...localCities];
    const [moved] = updated.splice(from, 1);
    updated.splice(index, 0, moved);
    setLocalCities(updated);
  };

  const onDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleSave = async () => {
    try {
      const payload = {
        upn,
        timezones: localCities.map((c, i) => ({
          navbar_timezone_id: c.id || c.navbar_timezone_id, // handle both new and existing timezones
          display_order: i + 1,
        })),
      };

      await saveUserTimezones(payload);

      const updated = await getUserTimezones(upn);
      dispatch(setTimezones(updated));
      setSavedCities(updated);
      setLocalCities(updated);

      setManageOpen(false);
      setHiddenOpen(false);
    } catch (err) {
      console.error("Failed to save timezone order:", err);
    }
  };

  const formatters = useMemo(() => {
    const locale = i18n.language || "en";

    return cities.reduce<Record<string, any>>((acc, c) => {
      acc[c.key] = {
        time24: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),

        time12: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),

        ampm: new Intl.DateTimeFormat("en-US", {
          timeZone: c.timezone,
          hour: "numeric",
          hour12: true,
        }),

        hour: new Intl.DateTimeFormat("en-US", {
          timeZone: c.timezone,
          hour: "numeric",
          hour12: false,
        }),

        date: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          weekday: "long",
          month: "short",
          day: "2-digit",
          year: "numeric",
        }),
      };
      return acc;
    }, {});
  }, [i18n.language, cities]);

  useEffect(() => {
    const update = () => {
      const now = new Date();

      const nextTimes24: Record<string, string> = {};
      const nextTimes12: Record<string, string> = {};
      const nextOffice: Record<string, boolean> = {};
      const nextDates: Record<string, string> = {};

      cities.forEach((city) => {
        const f = formatters[city.key];

        const t24 = f.time24.format(now);
        const t12 = f.time12.format(now);

        const localHour = Number(f.hour.format(now));

        nextTimes24[city.key] = t24;
        nextTimes12[city.key] = t12;
        nextDates[city.key] = f.date.format(now);
        nextOffice[city.key] = localHour >= city.officeStartHour && localHour < city.officeEndHour;
      });

      setTimes24(nextTimes24);
      setTimes12(nextTimes12);
      setDates(nextDates);
      setOfficeHours(nextOffice);
    };

    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [formatters, i18n.language]);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (!hiddenRef.current) return;
      if (!hiddenRef.current.contains(e.target as Node)) {
        setHiddenOpen(false);

        // ✅ reset manage mode
        setManageOpen(false);

        // ✅ discard unsaved changes
        setLocalCities(cities);
      }
    };

    if (hiddenOpen) document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [hiddenOpen]);

  const renderCity = (city: Timezone) => {
    const label = city.label;

    return (
      <div key={city.key} className="relative group z-50 text-center">
        <span className="block text-[10px] font-semibold opacity-60">
          {label.toLocaleUpperCase()}
        </span>
        <span className="block text-[14px] font-semibold mt-0.5">{times24[city.key]}</span>
        <div className="pointer-events-none absolute left-1/2 top-full hidden -translate-x-1/2 pt-3 group-hover:block">
          <div className="w-56 rounded-xl border p-4 text-center shadow-xl backdrop-blur bg-[var(--panel-bg)] text-[var(--panel-text)] border-[var(--panel-sep)]">
            <p className="text-xs font-semibold opacity-60">
              {label}, {city.country}
            </p>
            <p className="mt-2 text-3xl font-bold">{times12[city.key]}</p>
            <p className="text-xs mt-1 opacity-70">{dates[city.key]}</p>

            <div className="mt-4 flex items-center justify-center gap-2 text-sm">
              <span
                className={`h-2 w-2 rounded-full ${
                  officeHours[city.key] ? "bg-green-500" : "bg-gray-400"
                }`}
              />

              <span>
                {officeHours[city.key]
                  ? t("timezoneBar.officeHoursNow", {
                      defaultValue: "Office hours now",
                    })
                  : t("timezoneBar.officeHoursClosed", {
                      defaultValue: "Outside office hours",
                    })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="timezone-bar flex items-center gap-6">
      {visibleCities.map(renderCity)}

      <div className="relative">
        <span onClick={() => setHiddenOpen((v) => !v)} className="cursor-pointer text-lg font-bold">
          ...
        </span>

        <div
          ref={hiddenRef}
          className={`absolute right-0 top-full mt-2 ${hiddenOpen ? "block" : "hidden"}`}
        >
          <div className="w-60 rounded-lg border bg-[var(--panel-bg)] text-[var(--panel-text)] border-[var(--panel-sep)] shadow-lg">
            {!manageOpen ? (
              <>
                {/* ✅ ONLY HIDDEN CITIES */}
                {hiddenCities.map((city) => (
                  <div key={city.key} className="relative group">
                    <div className="flex justify-between px-3 py-2 text-sm hover:bg-gray-100">
                      <span className="text-xs">{city.label.toLocaleUpperCase()}</span>
                      <span>{times24[city.key]}</span>
                    </div>

                    {/* ✅ SAME HOVER AS NAVBAR */}
                    <div className="pointer-events-none absolute left-full top-0 z-50 hidden ml-3 group-hover:block">
                      <div className="w-56 rounded-xl border p-4 text-center shadow-xl backdrop-blur bg-[var(--panel-bg)] text-[var(--panel-text)] border-[var(--panel-sep)]">
                        <p className="text-xs font-semibold opacity-60">
                          {city.label}, {city.country}
                        </p>

                        <p className="mt-2 text-3xl font-bold">{times12[city.key]}</p>
                        <p className="text-xs mt-1 opacity-70">{dates[city.key]}</p>

                        <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                          <span
                            className={`h-2 w-2 rounded-full ${
                              officeHours[city.key] ? "bg-green-500" : "bg-gray-400"
                            }`}
                          />

                          <span>
                            {officeHours[city.key]
                              ? t("timezoneBar.officeHoursNow", {
                                  defaultValue: "Office hours now",
                                })
                              : t("timezoneBar.officeHoursClosed", {
                                  defaultValue: "Outside office hours",
                                })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* ✅ SINGLE BUTTON */}
                <div className="border-t">
                  <button
                    onClick={() => setManageOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-gray-100"
                  >
                    <Settings01 className="w-4 h-4" />
                    {t("timezoneBar.settings", { defaultValue: "Settings" })}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* ✅ FULL LIST FOR REORDER */}
                <div>
                  {localCities.map((city, index) => (
                    <div
                      key={city.key}
                      draggable
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver}
                      onDrop={onDrop(index)}
                      className="flex justify-between px-2 py-1.5 text-xs border-b cursor-move bg-gray-50"
                    >
                      <span>{city.label}</span>
                      <span>☰</span>
                    </div>
                  ))}
                </div>

                {/* ✅ ACTIONS */}
                <div className="flex justify-end gap-2 p-2 border-t">
                  <button
                    onClick={() => {
                      setManageOpen(false);
                      setHiddenOpen(false);
                      setLocalCities(cities);
                    }}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                  >
                    {t("timezoneBar.cancel", { defaultValue: "Cancel" })}
                  </button>

                  <button
                    onClick={handleSave}
                    className="px-2 py-1 text-sm bg-blue-600 text-white rounded"
                  >
                    {t("timezoneBar.save", { defaultValue: "Save" })}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimezoneBar;
